import {
  getDilutionBatchPipettingAsPdfUrl,
  getDilutionBatchPipettingAsXlsxUrl,
  getDilutionPipettingAsPdfUrl,
  getDilutionPipettingAsXlsxUrl,
  useDilutionDeletePlate,
  useDilutionDeleteWell,
  useDilutionDilute,
  useDilutionNextWell,
  useDilutionOptions,
  useDilutionPlate,
  useDilutionSamples,
} from "@api/generated/dilution/dilution";
import type { DilutionSamplesParams, DilutionSource, PipettingRow, Slot, SourceSample } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { useInvalidate } from "@app/queryClient";
import { hasRole } from "@app/workspace";
import { LinePicker } from "@features/genealogy/GenotypeParts";
import { DilutionWells } from "@features/genetech/DilutionWells";
import { hint } from "@i18n";
import "@features/genetech/genetech.css";
import { LayoutLegend, LayoutMap, LayoutPager, slotAfter, slotText } from "@features/genetech/LayoutMap";
import { Autocomplete, Button, Checkbox, Loader, NumberInput, SegmentedControl, Select } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { ConfirmModal } from "@shell/Dialogs";
import { createFileRoute } from "@tanstack/react-router";
import { ActionMenu } from "@ui/ActionMenu";
import { formatNumber, num } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { pickedRows } from "@ui/picks";
import { RemovableChip } from "@ui/RemovableChip";
import { MissingList } from "@ui/RunBar";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { usePicks } from "@ui/table/usePicks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/lab/dna-dilution")({ component: DilutionPage });

const sourceKey = (s: DilutionSource) => `${s.batch}|${s.box ?? ""}`;
const vol = (v: number | null | undefined) => formatNumber(v, 1);
const sampleId = (s: SourceSample) => s.id;
const sampleLabel = (s: SourceSample) => s.name ?? String(s.id);

/** After wells were placed or deleted: the sources, the samples, the plates and the next well read again. */
const DILUTION_CHANGED = /^\/api\/v1\/dilution\//;

type Mode = "box" | "line";
type Line = { paz: number; sid: number; title: string };
type Source = Pick<DilutionSamplesParams, "batch" | "paz" | "sid">;

/** Whether a samples query (its params) reads the same source: a batch or a line. */
function sameSource(params: unknown, from: Source | null): boolean {
  const p = params as DilutionSamplesParams | undefined;
  return !!p && !!from && p.batch === from.batch && p.paz === from.paz && p.sid === from.sid;
}

/** Molekuláris labor → DNS-hígítás (the legacy frmDNADilution): a batch's (box's) samples, or one genotype's samples
 *  of every batch, ticked, placed on the 96-well plates with their working solution after the last well of all, on a
 *  new plate, or from a free well clicked on the plate map; the plate map and its pipetting list (stock and water per
 *  well; a well can be freed), the batch's list across its plates, the wells found by person or genotype. A sample on
 *  a plate already is placed again only when ticked by hand. */
function DilutionPage() {
  const { t } = useTranslation();
  const me = useUserMe().data;
  const canEdit = hasRole(me?.roles, "labor");
  const options = useDilutionOptions().data;
  const [mode, setMode] = useState<Mode>("box");
  const [key, setKey] = useState<string | null>(null);
  const [line, setLine] = useState<Line | null>(null);
  const source = options?.sources.find((s) => sourceKey(s) === key) ?? null;
  const [target, setTarget] = useState<number | null>(20);
  const [volume, setVolume] = useState<number | null>(100);
  const [person, setPerson] = useState<string | null>(null);
  const [newPlate, setNewPlate] = useState(false);
  const [start, setStart] = useState<Slot | null>(null); // a free well clicked on the map (else the server's next)
  // the samples ticked, in the order ticked (the wells follow it); null: the source's samples not on a plate yet
  const [picked, setPicked] = useState<number[] | null>(null);
  const [viewed, setViewed] = useState<number | null>(null);
  const [panel, setPanel] = useState<"samples" | "pipetting" | "wells">("samples");
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState<PipettingRow | null>(null);
  const next = useDilutionNextWell(
    start ? { plate: start.no, row: start.row, col: start.col } : newPlate ? { new_plate: true } : undefined,
  ).data;
  const plateNo = viewed ?? next?.no ?? null;
  const plate = useDilutionPlate(plateNo ?? 0, { query: { enabled: plateNo != null } }).data;
  const from: Source | null = mode === "line" ? (line ? { paz: line.paz, sid: line.sid } : null) : source ? { batch: source.batch } : null;
  const [solutionTarget] = useDebouncedValue(target, 400); // the samples are read again after a pause, not per keystroke
  const [solutionVolume] = useDebouncedValue(volume, 400);
  const found = useDilutionSamples(
    { ...from, target: solutionTarget || undefined, volume: solutionVolume || undefined },
    // the previous samples stay on screen only while the source is the same (a new target or volume): another
    // batch's or genotype's samples must not be ticked, and placed, under the new one
    { query: { enabled: !!from, placeholderData: (prev, q) => (sameSource(q?.queryKey[1], from) ? prev : undefined) } },
  ).data;
  const reading = !!from;
  const box = source?.box;
  const samples = useMemo(
    () => (!reading ? [] : mode === "line" ? (found ?? []) : (found ?? []).filter((s) => s.box_no === box)),
    [reading, mode, found, box],
  );
  const batchPlated = !!source && (options?.sources ?? []).some((s) => s.batch === source.batch && s.plated > 0);
  const ticked = useMemo(() => picked ?? samples.filter((s) => !s.plates.length).map(sampleId), [picked, samples]);
  const picks = usePicks({ picks: ticked, keyOf: sampleId, onPicks: setPicked, label: sampleLabel });
  const chosen = useMemo(() => pickedRows(samples, ticked, sampleId), [ticked, samples]);
  const again = chosen.filter((s) => s.plates.length).length;
  const mine = new Set(samples.map((s) => s.id));
  const changed = useInvalidate(DILUTION_CHANGED);
  const choose = (k: string | null) => {
    setKey(k);
    setPicked(null);
  };
  const pickLine = (l: Line | null) => {
    setLine(l);
    setPicked(null);
  };
  const startAt = (s: Slot | null) => {
    setStart(s);
    if (s) setNewPlate(false);
  };
  const dilute = useDilutionDilute({
    mutation: {
      onSuccess: (out) => {
        notifications.show({ color: "green", message: t("dnaDilution.placed", { n: out.placed.length }) });
        setNewPlate(false);
        setStart(null);
        setPicked(null);
        setViewed(out.placed.at(-1)?.well.no ?? null);
        setPanel("pipetting");
        changed();
      },
    },
  });
  const removePlate = useDilutionDeletePlate({
    mutation: {
      onSuccess: () => {
        setConfirming(false);
        setViewed(null);
        changed();
        notifications.show({ color: "green", message: t("dnaDilution.plateDeleted") });
      },
    },
  });
  const removeWell = useDilutionDeleteWell({
    mutation: {
      onSuccess: () => {
        setRemoving(null);
        changed();
        notifications.show({ color: "green", message: t("dnaDilution.wellDeleted") });
      },
    },
  });
  const contact = person ?? me?.full_name ?? "";
  const ready = canEdit && chosen.length > 0 && !!target && !!volume;
  const after = plate && slotAfter(plate.no, plate.rows, plate.cols, plate.wells.at(-1)?.well);
  const own = useMemo<Column<SourceSample>[]>(
    () => [
      ...(mode === "line"
        ? [
            {
              id: "batch",
              header: t("dnaDilution.batch"),
              value: (s) => s.batch,
              className: "sm",
            } as Column<SourceSample>,
          ]
        : []),
      {
        id: "box",
        header: t("dnaDilution.box"),
        value: (s) => (s.box ? slotText(s.box) : null),
        className: "mono sm",
      },
      { id: "name", header: t("dnaDilution.genotype"), value: (s) => s.name },
      {
        id: "concentration",
        header: t("dnaDilution.stockConcentration"),
        value: (s) => s.concentration,
        cell: (s) => vol(s.concentration),
        numeric: true,
      },
      {
        id: "stock",
        header: t("dnaDilution.solution"),
        value: (s) => s.stock,
        cell: (s) => vol(s.stock),
        numeric: true,
        className: "mono",
      },
      { id: "water", header: t("dnaDilution.water"), value: (s) => s.water, cell: (s) => vol(s.water), numeric: true, className: "mono" },
      { id: "plates", header: t("dnaDilution.onPlate"), value: (s) => s.plates.join(", "), className: "sm mu" },
    ],
    [t, mode],
  );
  const sampleColumns = useMemo(() => [...picks.columns, ...own], [picks.columns, own]);
  const sampleTable = useDataTable("lab.dilution.samples", sampleColumns, samples, { versions: picks.versions });
  const wellColumns = useMemo<Column<PipettingRow>[]>(
    () => [
      { id: "well", header: t("dnaDilution.well"), value: (r) => `${r.well.row}${r.well.col}`, className: "mono" },
      { id: "name", header: t("dnaDilution.genotype"), value: (r) => r.name },
      {
        id: "box",
        header: t("dnaDilution.box"),
        value: (r) => (r.box ? slotText(r.box) : null),
        className: "mono sm",
      },
      {
        id: "concentration",
        header: t("dnaDilution.stockConcentration"),
        value: (r) => r.stock_concentration,
        cell: (r) => vol(r.stock_concentration),
        numeric: true,
      },
      {
        id: "stock",
        header: t("dnaDilution.solution"),
        value: (r) => r.stock,
        cell: (r) => vol(r.stock),
        numeric: true,
        className: "mono",
      },
      { id: "water", header: t("dnaDilution.water"), value: (r) => r.water, cell: (r) => vol(r.water), numeric: true, className: "mono" },
      ...(canEdit
        ? [
            {
              id: "delete",
              header: "",
              name: t("dnaDilution.deleteWell"),
              fixed: true,
              cell: (r) => (
                <button
                  type="button"
                  className="chip"
                  aria-label={t("dnaDilution.deleteWellNamed", { well: `${r.well.row}${r.well.col}` })}
                  onClick={() => setRemoving(r)}
                >
                  ✕
                </button>
              ),
            } as Column<PipettingRow>,
          ]
        : []),
    ],
    [t, canEdit],
  );
  const wellTable = useDataTable("lab.dilution.pipetting", wellColumns, plate?.wells);
  return (
    <div className="page fill">
      <PageHeader title={t("dnaDilution.title")} subtitle={t("dnaDilution.subtitle")} />
      <section className="card pad">
        <div className="hstack" style={{ gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <SegmentedControl
            size="xs"
            value={mode}
            onChange={(v) => {
              setMode(v as Mode);
              setPicked(null);
            }}
            data={[
              { value: "box", label: t("dnaDilution.modeBox") },
              { value: "line", label: t("dnaDilution.modeGenotype") },
            ]}
          />
          {mode === "box" ? (
            <Select
              id="dilution-source"
              placeholder={hint("searchSelect")}
              size="xs"
              w={260}
              searchable
              label={t("dnaDilution.source")}
              value={key}
              onChange={choose}
              allowDeselect={false}
              data={(options?.sources ?? []).map((s) => ({
                value: sourceKey(s),
                label: t("dnaDilution.sourceName", { batch: s.batch, box: s.box ?? "—", n: s.samples, plated: s.plated }),
              }))}
            />
          ) : line ? (
            <div className="stack" style={{ gap: 2 }}>
              <span className="sm mu">{t("dnaDilution.genotype")}</span>
              <RemovableChip removeLabel={t("dnaDilution.otherGenotype")} onRemove={() => pickLine(null)}>
                {line.title}
              </RemovableChip>
            </div>
          ) : (
            <LinePicker label={t("dnaDilution.genotype")} onPick={(k, title) => pickLine({ ...k, title })} />
          )}
          <NumberInput
            id="dilution-target"
            size="xs"
            w={130}
            label={t("dnaDilution.targetConcentration")}
            min={0}
            value={target ?? ""}
            onChange={(v) => setTarget(num(v))}
          />
          <NumberInput
            id="dilution-volume"
            size="xs"
            w={130}
            label={t("dnaDilution.finalVolume")}
            min={0}
            value={volume ?? ""}
            onChange={(v) => setVolume(num(v))}
          />
          <Autocomplete
            size="xs"
            w={180}
            label={t("dnaDilution.performedBy")}
            value={contact}
            onChange={setPerson}
            data={options?.persons ?? []}
          />
          <Checkbox
            size="xs"
            label={t("dnaDilution.newPlate")}
            checked={newPlate}
            onChange={(e) => {
              setNewPlate(e.currentTarget.checked);
              if (e.currentTarget.checked) setStart(null);
            }}
          />
          {start && (
            <RemovableChip tone="y" removeLabel={t("dnaDilution.clearStart")} onRemove={() => setStart(null)}>
              {t("dnaDilution.start", { position: slotText(start) })}
            </RemovableChip>
          )}
          <span className="grow" />
          {again > 0 && <span className="sm lab-again">{t("dnaDilution.alreadyPlated", { n: again })}</span>}
          <MissingList
            requirements={[
              mode === "box"
                ? { label: t("dnaDilution.needSource"), done: !!source, field: "dilution-source" }
                : { label: t("dnaDilution.needGenotype"), done: !!line },
              { label: t("dnaDilution.needSample"), done: !from || chosen.length > 0 },
              { label: t("dnaDilution.needTarget"), done: !!target, field: "dilution-target" },
              { label: t("dnaDilution.needVolume"), done: !!volume, field: "dilution-volume" },
              { label: t("runBar.needsWriteAccess"), done: canEdit },
            ]}
          />
          <Button
            disabled={!ready}
            loading={dilute.isPending}
            onClick={() =>
              target &&
              volume &&
              dilute.mutate({
                data: {
                  samples: chosen.map((s) => s.id),
                  new_plate: newPlate,
                  start,
                  again: again > 0,
                  target,
                  volume,
                  person: contact || null,
                },
              })
            }
          >
            {start
              ? t("dnaDilution.placeFrom", { n: chosen.length, plate: start.no, well: `${start.row}${start.col}` })
              : t("dnaDilution.place", { n: chosen.length, plate: next?.no ?? "" })}
          </Button>
        </div>
        {mode === "box" && batchPlated && source && (
          <div className="hstack sm" style={{ gap: 8, marginTop: 8 }} title={t("dnaDilution.batchListHelp")}>
            <span className="mu">{t("dnaDilution.batchList")}</span>
            <a href={getDilutionBatchPipettingAsXlsxUrl({ batch: source.batch })}>Excel</a>
            <a href={getDilutionBatchPipettingAsPdfUrl({ batch: source.batch })}>PDF</a>
          </div>
        )}
      </section>
      <div className="fill-row">
        <section className="card pad stack grow" style={{ gap: 10, minWidth: 0 }}>
          <div className="hstack" style={{ gap: 8 }}>
            <LayoutPager
              no={plateNo}
              label={t("dnaDilution.plate")}
              prev={t("dnaDilution.previousPlate")}
              next={t("dnaDilution.nextPlate")}
              onChange={setViewed}
            />
            {plate && <span className="chip">{t("dna.occupied", { n: plate.wells.length, size: plate.rows.length * plate.cols })}</span>}
            <span className="grow" />
            <span className="sm mu">{t("dnaDilution.orderHint")}</span>
            {canEdit && plate && plateNo != null && (
              <ActionMenu
                size="xs"
                actions={[
                  {
                    label: t("dnaDilution.continueOnPlate"),
                    icon: "arrowRight",
                    hidden: !after || plate.wells.length === 0,
                    onClick: () => startAt(after ?? null),
                  },
                  {
                    label: t("dnaDilution.deletePlateNumbered", { no: plateNo }),
                    icon: "trash",
                    danger: true,
                    hidden: plate.wells.length === 0,
                    onClick: () => setConfirming(true),
                  },
                ]}
              />
            )}
          </div>
          {plate && (
            <LayoutMap
              no={plate.no}
              rows={plate.rows}
              cols={plate.cols}
              next={next ?? null}
              onPick={canEdit ? startAt : undefined}
              cells={plate.wells.map((w) => ({
                row: w.well.row,
                col: w.well.col,
                text: w.box ? String(w.box.no) : "",
                title: `${w.name ?? ""} · ${t("dnaDilution.fromBox", { no: w.box?.no ?? "—" })}`,
                mine: w.sample != null && mine.has(w.sample),
              }))}
            />
          )}
          {!plate && plateNo != null && <Loader size="sm" />}
          <LayoutLegend
            mine={t("dnaDilution.legendSource")}
            taken={t("dnaDilution.legendOther")}
            next={t("dnaDilution.legendNext")}
            free={t("dna.legendFree")}
          />
          <p className="sm mu">
            {t("dnaDilution.legend")} {canEdit && t("dnaDilution.legendStart")}
          </p>
        </section>
        <section className="card stack lab-list" style={{ gap: 0 }}>
          <div className="hstack panel-head" style={{ gap: 8 }}>
            <SegmentedControl
              size="xs"
              value={panel}
              onChange={(v) => setPanel(v as typeof panel)}
              data={[
                { value: "samples", label: t("dnaDilution.samples", { n: chosen.length, total: samples.length }) },
                { value: "pipetting", label: t("dnaDilution.pipettingList") },
                { value: "wells", label: t("dnaDilution.search") },
              ]}
            />
            <span className="grow" />
            {panel === "pipetting" && plateNo && (
              <>
                <a className="sm" href={getDilutionPipettingAsXlsxUrl(plateNo)}>
                  Excel
                </a>
                <a className="sm" href={getDilutionPipettingAsPdfUrl(plateNo)}>
                  PDF
                </a>
              </>
            )}
          </div>
          {panel === "wells" ? (
            <DilutionWells
              persons={options?.persons ?? []}
              onPlate={(no) => {
                setViewed(no);
                setPanel("pipetting");
              }}
            />
          ) : panel === "samples" ? (
            <DataTable
              table={sampleTable}
              rowKey={(s) => s.id}
              className="grow"
              rowProps={(s) => ({
                ...picks.rowProps(s, s.warning ? "lab-warn" : undefined),
                title: s.warning ? t(`dnaDilution.warning.${s.warning}`) : undefined,
              })}
              empty={
                !from
                  ? mode === "box"
                    ? t("dnaDilution.selectSource")
                    : t("dnaDilution.selectGenotype")
                  : mode === "line" && found
                    ? t("dnaDilution.noDnaSample")
                    : undefined
              }
            />
          ) : (
            <DataTable
              table={wellTable}
              rowKey={(r) => r.id}
              className="grow"
              rowProps={(r) => ({
                className: r.warning ? "lab-warn" : undefined,
                title: r.warning ? t(`dnaDilution.warning.${r.warning}`) : undefined,
              })}
              empty={plate && t("dnaDilution.empty")}
            />
          )}
          {panel !== "wells" && <p className="sm mu inset">{t("dnaDilution.formula")}</p>}
        </section>
      </div>
      {confirming && plateNo != null && (
        <ConfirmModal
          title={t("dnaDilution.deletePlate")}
          text={t("dnaDilution.deleteConfirm", { no: plateNo, n: plate?.wells.length ?? 0 })}
          confirm={t("dnaDilution.deletePlate")}
          danger
          loading={removePlate.isPending}
          onConfirm={() => removePlate.mutate({ plate: plateNo })}
          onClose={() => setConfirming(false)}
        />
      )}
      {removing && (
        <ConfirmModal
          title={t("dnaDilution.deleteWell")}
          text={t("dnaDilution.deleteWellConfirm", {
            plate: removing.well.no,
            well: `${removing.well.row}${removing.well.col}`,
            name: removing.name ?? "—",
          })}
          confirm={t("dnaDilution.deleteWell")}
          danger
          loading={removeWell.isPending}
          onConfirm={() => removeWell.mutate({ id: removing.id })}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  );
}
